/* ==========================================================================
   FR cart email-capture reminder  —  assets/fr-cart-email-banner.js

   One global controller for every .fr-cart-banner on the page. The cart
   drawer is re-rendered by xo-cart-will-change on every cart change, and
   script tags injected that way never execute — so nothing here may live on
   the banner element itself. Click handling is delegated at the document, and
   a debounced MutationObserver re-evaluates banners that reappear.

   Visibility (all must hold, re-checked on every cart change):
     - not dismissed this session
     - not already subscribed (Shopify Forms post-submit marker, or a signup
       completed through this banner)
     - no discount already on the cart

   See snippets/fr-cart-email-banner.liquid for the mechanism notes.
   ========================================================================== */

(function () {
  if (window.__frCartBannerInit) return;
  window.__frCartBannerInit = true;

  var DISMISS_KEY = 'fr_cart_banner_dismissed';
  var SIGNED_KEY = 'fr_cart_banner_signed_up';

  /* Shopify Forms writes `${formId}-dismissed-after-submit` ONLY after a
     successful submit. Its `-dismissed-in-session` / `-minimized-in-session`
     siblings mean the popup was merely closed and must not count here. */
  var FORMS_SUBMITTED_RE = /^\d+-dismissed-after-submit$/;

  /* Confirmation stays on screen for the rest of this page view, then the
     banner is suppressed entirely on subsequent views. */
  var confirmedThisView = null;

  // ---------------------------------------------------------------- storage

  function sessGet(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  }

  function sessSet(key, value) {
    try { sessionStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }

  function formsAlreadySubmitted() {
    try {
      for (var i = 0; i < localStorage.length; i++) {
        if (FORMS_SUBMITTED_RE.test(localStorage.key(i))) return true;
      }
    } catch (e) { /* storage blocked */ }
    return false;
  }

  function alreadySubscribed() {
    return sessGet(SIGNED_KEY) === '1' || formsAlreadySubmitted();
  }

  // ------------------------------------------------------------- cart state

  var cartCache = null;
  var cartCachedAt = 0;
  var cartInFlight = null;

  function readCart(force) {
    var now = new Date().getTime();
    if (!force && cartCache && now - cartCachedAt < 2000) {
      return Promise.resolve(cartCache);
    }
    if (cartInFlight) return cartInFlight;

    cartInFlight = fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartCache = cart;
        cartCachedAt = new Date().getTime();
        cartInFlight = null;
        return cart;
      })
      .catch(function () {
        cartInFlight = null;
        return null;
      });

    return cartInFlight;
  }

  function cartHasDiscount(cart, el) {
    if (cart) {
      if (cart.discount_codes && cart.discount_codes.length > 0) return true;
      if (typeof cart.total_discount === 'number' && cart.total_discount > 0) return true;
      return false;
    }
    // cart.js unreachable — fall back to what Liquid rendered
    return !!(el && el.getAttribute('data-fr-has-discount') === 'true');
  }

  // ------------------------------------------------------------------ views

  function showOffer(el) {
    el.hidden = false;
    el.querySelector('[data-fr-offer]').hidden = false;
    el.querySelector('[data-fr-done]').hidden = true;
  }

  function showDone(el, message) {
    el.hidden = false;
    el.querySelector('[data-fr-offer]').hidden = true;
    el.querySelector('[data-fr-inline]').hidden = true;
    var done = el.querySelector('[data-fr-done]');
    done.textContent = message;
    done.hidden = false;
  }

  function hide(el) {
    el.hidden = true;
  }

  function refresh(el) {
    if (confirmedThisView) {
      showDone(el, confirmedThisView);
      return;
    }
    if (sessGet(DISMISS_KEY) === '1' || alreadySubscribed()) {
      hide(el);
      return;
    }
    readCart(false).then(function (cart) {
      if (cartHasDiscount(cart, el)) hide(el);
      else showOffer(el);
    });
  }

  function refreshAll() {
    var nodes = document.querySelectorAll('[data-fr-cart-banner]');
    for (var i = 0; i < nodes.length; i++) refresh(nodes[i]);
  }

  // -------------------------------------------------- Shopify Forms re-open

  function formsEmbed() {
    var el = document.querySelector('shopify-forms-embed');
    return el && el.shadowRoot ? el : null;
  }

  /* Open the existing capture. Preferred path is the Shopify Forms popup:
     where the embed is mounted, closing it leaves a teaser button behind and
     clicking that teaser returns the form to its email step.

     The teaser click is handled by React, so the form is NOT in the DOM on
     the next statement — checking synchronously reports failure and opens the
     inline field on top of a popup that is about to appear. Confirm on a
     short delay instead, and only then decide whether to fall back. */
  function openCapture(el) {
    var embed = formsEmbed();
    if (!embed) { openInline(el); return; }

    var root = embed.shadowRoot;

    function emailFieldPresent() {
      return !!root.querySelector('[data-testid="field-email"]');
    }

    if (emailFieldPresent()) { watchFormsForSuccess(); return; }

    var teaser = root.querySelector('[data-testid="form-teaser"]');
    if (!teaser) { openInline(el); return; }

    teaser.click();

    setTimeout(function () {
      if (emailFieldPresent()) watchFormsForSuccess();
      else openInline(el);
    }, 400);
  }

  /* Watch the popup for a completed signup. Shopify Forms exposes no event,
     so we watch its shadow DOM for change and re-test the post-submit marker
     it writes. A bounded interval covers the case where the popup unmounts
     before the observer fires. */
  var watching = false;

  function watchFormsForSuccess() {
    if (watching) return;
    watching = true;

    var embed = formsEmbed();
    var observer = null;
    var ticks = 0;

    function settle() {
      if (!alreadySubscribed()) return false;
      if (observer) observer.disconnect();
      clearInterval(timer);
      watching = false;
      onSignedUp();
      return true;
    }

    if (embed) {
      observer = new MutationObserver(function () { settle(); });
      observer.observe(embed.shadowRoot, {
        childList: true, subtree: true, attributes: true
      });
    }

    var timer = setInterval(function () {
      ticks++;
      if (settle()) return;
      if (ticks > 90) { // ~3 minutes
        if (observer) observer.disconnect();
        clearInterval(timer);
        watching = false;
      }
    }, 2000);
  }

  // ------------------------------------------------------------- signup end

  /* Say whichever thing is actually true, rather than promising either.
     The popup's flow is a discount claim, so the code is normally applied
     straight to the cart; if it is not, the code comes by email instead. */
  function onSignedUp() {
    sessSet(SIGNED_KEY, '1');

    readCart(true).then(function (cart) {
      var message = cartHasDiscount(cart, null)
        ? '15% off applied — you’ll see it at checkout.'
        : 'Check your inbox — your 15% code is on the way.';

      confirmedThisView = message;
      refreshAll();
    });
  }

  // ------------------------------------------------------- inline fallback

  function openInline(el) {
    var inline = el.querySelector('[data-fr-inline]');
    inline.hidden = false;
    var input = inline.querySelector('.fr-cart-banner__input');
    if (input) setTimeout(function () { input.focus(); }, 50);
  }

  function submitInline(el, form) {
    var submit = form.querySelector('.fr-cart-banner__submit');
    var error = el.querySelector('[data-fr-error]');
    var code = el.getAttribute('data-fr-code');

    error.hidden = true;
    if (submit) submit.disabled = true;

    fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    })
      .then(function (res) {
        var ok = res.ok && res.url && res.url.indexOf('customer_posted=true') > -1;
        if (!ok && !res.ok) throw new Error('signup failed');

        /* Mirror what the popup's claim flow does: put the existing 15OFF
           code on the cart. Nothing here creates or edits a discount. */
        return fetch('/discount/' + encodeURIComponent(code), {
          credentials: 'same-origin'
        }).catch(function () { /* code may simply not apply; cart read decides */ });
      })
      .then(function () {
        onSignedUp();
      })
      .catch(function () {
        if (submit) submit.disabled = false;
        error.textContent = 'That didn’t go through. Please try again.';
        error.hidden = false;
      });
  }

  // ---------------------------------------------------------------- wiring

  document.addEventListener('click', function (event) {
    var openBtn = event.target.closest && event.target.closest('[data-fr-open]');
    if (openBtn) {
      var el = openBtn.closest('[data-fr-cart-banner]');
      if (!el) return;
      event.preventDefault();
      openCapture(el);
      return;
    }

    var closeBtn = event.target.closest && event.target.closest('[data-fr-dismiss]');
    if (closeBtn) {
      event.preventDefault();
      sessSet(DISMISS_KEY, '1');
      var nodes = document.querySelectorAll('[data-fr-cart-banner]');
      for (var i = 0; i < nodes.length; i++) hide(nodes[i]);
    }
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.classList || !form.classList.contains('fr-cart-banner__form')) return;
    var el = form.closest('[data-fr-cart-banner]');
    if (!el) return;
    event.preventDefault();
    submitInline(el, form);
  });

  /* The drawer is re-rendered wholesale on cart change; re-evaluate whenever
     new banner markup lands in the DOM. Debounced so a burst of cart updates
     costs one pass. */
  var pending = null;
  var bodyObserver = new MutationObserver(function () {
    if (pending) return;
    pending = setTimeout(function () {
      pending = null;
      refreshAll();
    }, 250);
  });

  function start() {
    refreshAll();
    if (document.body) {
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  ['turbo:load', 'turbo:render', 'xo:load', 'pageshow'].forEach(function (ev) {
    window.addEventListener(ev, function () { refreshAll(); });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
